import {
  FileObject,
  FileStatus,
  SelectedAsset,
} from '@/stores/contentStudioStore';
import {Asset} from '../components/asset-table/types';

/**
 * Converts a record of FileObject to an array of Asset objects with 'uploading' status.
 * This function is used to display files that are currently being uploaded in the UI.
 *
 * @param files - The files from the store
 * @returns An array of Asset objects with 'uploading' status
 */
export const displayFilesAsUploading = (
  files: Record<string, FileObject>
): Asset[] => {
  return Object.values(files)
    .filter((file) => file.status !== FileStatus.ERROR)
    .map((file) => {
      return {
        id: file.id || '',
        name: file.file.name,
        metadata: {
          durationMillis: file.metadata?.duration_millis || 0,
          fileSizeBytes: file.file.size || 0,
          height: file.metadata?.height || 0,
          width: file.metadata?.width || 0,
        },
        type: 'upload',
        status: 'in_progress',
        progress: file.progress,
      };
    });
};

/**
 * Checks if an asset corresponds to a file in the files record and updates its status accordingly.
 * If the asset is found in the files record, it deletes the file and updates the selected asset's status.
 *
 * @param asset - The asset to check
 * @param files - The files from the store
 * @param selectedAssets - The selected assets from the store
 * @param updateSelectedAsset - Function to update a selected asset's properties
 * @param deleteFile - Function to delete a file by its name
 * @returns The original asset
 */
const checkAssetForFileOrSelectedAsset = (
  asset: Asset,
  files: Record<string, FileObject>,
  selectedAssets: SelectedAsset[]
): {
  asset: Asset;
  hasFile: boolean;
  fileToDelete: string | undefined;
  isSelected: boolean;
} => {
  const foundFile = Object.values(files).find((file) => file.id === asset.id);
  const selectedAssetFound = selectedAssets.find(
    (selectedAsset) => selectedAsset.id === asset.id
  );

  const hasAssetThumbnail = !!asset.thumbnailUrl && asset.thumbnailUrl !== '';

  return {
    asset,
    hasFile: !!foundFile,
    fileToDelete: hasAssetThumbnail ? foundFile?.file.name : undefined,
    isSelected: !!selectedAssetFound && hasAssetThumbnail,
  };
};

/**
 * Processes a paginated array of assets to check if any files are still uploading.
 * Assets with 'in_progress' have no thumbnail, so are filtered out. The rest of the assets are checked if they correspond to a file in the files record, so it can be deleted.
 *
 * @param pages - The paginated assets
 * @param selectedAssets - The selected assets from the store
 * @param updateSelectedAsset - Function to update a selected asset's properties
 * @param deleteFile - Function to delete a file by its name
 * @param files - The files from the store
 * @returns A flattened array of processed Asset objects
 */
export const checkIncomingAssets = (
  pages: Asset[][],
  selectedAssets: SelectedAsset[],
  files: Record<string, FileObject>
): {
  assetsToDisplay: Asset[];
  assetsToUpdate: Asset[];
  filesToRemove: string[];
} => {
  const checkedUploads = {
    assetsToDisplay: [] as Asset[],
    assetsToUpdate: [] as Asset[],
    filesToRemove: [] as string[],
  };
  pages.flatMap((page) =>
    page.map((asset) => {
      const {
        asset: checkedAsset,
        fileToDelete,
        isSelected,
        hasFile,
      } = checkAssetForFileOrSelectedAsset(asset, files, selectedAssets);
      if (isSelected) {
        checkedUploads.assetsToUpdate.push(checkedAsset);
      }
      if (fileToDelete) {
        checkedUploads.filesToRemove.push(fileToDelete);
      }
      if (!hasFile || fileToDelete) {
        checkedUploads.assetsToDisplay.push(checkedAsset);
      }
      return null;
    })
  );
  return checkedUploads;
};
